import { createContext, ReactElement, useContext, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';

type AlertMode = "success" | "error";

const AlertContext = createContext({
  success: (message: string) => {},
  error: (message: string) => {}
});

interface AlertProviderInterface {
  children: ReactElement;
}

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};

export const AlertProvider = ({ children }: AlertProviderInterface) => {
  const [open, setOpen] = useState<boolean>(false);
  const [alertMode, setAlertMode] = useState<AlertMode>("success");
  const [alertMessage, setAlertMessage] = useState<string>("");

  const valueProvider = useMemo(() => ({
    success: (message: string) => {
      setAlertMessage(message);
      setAlertMode("success");
      setOpen(true);
    },
    error: (message: string) => {
      setAlertMessage(message);
      setAlertMode("error");
      setOpen(true);
    }
  }), []);

  return (
    <AlertContext.Provider value={valueProvider}>
      <Snackbar
        anchorOrigin={{ 
          horizontal: "center", 
          vertical: "top" 
        }}
        autoHideDuration={5000}
        open={open}
        onClose={() => setOpen(false)} 
        sx={{ width: '600px', margin: 'auto' }}
      >
        <Alert 
          variant="filled"  
          severity={alertMode === "success" ? "success" : "error"}                 
          onClose={() => setOpen(false)} 
          sx={{ width: '100%' }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
      {children}
    </AlertContext.Provider>
  );
};